import { updateOrderSnapshot, useRegisterLoyaltyAndReassignOrder } from '@/entities/order';
import { type AccountLoyalty } from '@/shared/api/account';
import { type OrderLoyalty } from '@/shared/api/order';
import { useAsyncFn } from '@/shared/lib/hooks';
import { modalsService } from '@/shared/lib/modalsService';
import { getRequestContext } from '@/shared/lib/requestContext';
import { LoyaltyBanner } from './components/LoyaltyBanner';
import { LoyaltySwitcher } from './components/LoyaltySwitcher';

interface LoyaltyBlockUIProps {
    /**
     * Инфа по ПЛ из ордера
     */
    orderLoyalty: OrderLoyalty;

    /**
     * Инфа по ПЛ для текущего аккаунта
     */
    accountLoyalty: AccountLoyalty;
    /**
     * Флаг применения бонусов к заказу
     */
    isSelected: boolean;
    isLoggedIn: boolean;
    isRefreshing: boolean;
    onRefresh: () => Promise<unknown>;
    onChange: (value: boolean) => void;
    onRegister: VoidFunction;
    isLoadingRegister: boolean;
    registerError: unknown;
}

export const LoyaltyBlockUI = (props: LoyaltyBlockUIProps) => {
    const {
        orderLoyalty,
        accountLoyalty,
        isLoggedIn,
        isSelected,
        isLoadingRegister,
        isRefreshing,
        registerError,
        onRegister,
        onRefresh,
        onChange,
    } = props;

    //  При наличии прикрепленной ПЛ в ордере - выводим свитчер с применением бонусов
    if (orderLoyalty.is_registered_user) {
        return (
            <LoyaltySwitcher
                loyalty={orderLoyalty.registered_user_loyalty}
                isSelected={isSelected}
                isRefreshing={isRefreshing}
                onRefresh={onRefresh}
                onChange={onChange}
            />
        );
    }

    //  При отсутствии прикрепленной ПЛ в ордере, залогине и наличии ПЛ для текущего аккаунта - по клику применяем ПЛ от текущего аккаунта к ордеру
    if (isLoggedIn && accountLoyalty.isRegistered) {
        return <LoyaltyBanner isError={Boolean(registerError)} onClick={onRegister} isLoading={isLoadingRegister} />;
    }

    //  При отсутствии прикрепленной ПЛ в ордере, залогине и отсутствии ПЛ для текущего аккаунта - открываем модалку с регистрацией в ПЛ
    if (isLoggedIn) {
        return <LoyaltyBanner onClick={() => modalsService.onLoyaltyRegister('bonus-program')} />;
    }

    //  При отсутствии прикрепленной ПЛ в ордере, незалогине - открываем модалку с авторизацией
    if (!isLoggedIn) {
        return <LoyaltyBanner onClick={() => modalsService.onLoyaltyPromo()} />;
    }

    return null;
};

interface LoyaltyBlockProps {
    loyalty?: OrderLoyalty;
    isSelected: boolean;
    refetch: () => Promise<unknown>;
    onChange: (value: boolean) => void;
}

export const LoyaltyBlock = (props: LoyaltyBlockProps) => {
    const { loyalty, isSelected, refetch, onChange } = props;

    const {
        orderId,
        signature,
        storeSlug,
        account: { loyalty: accountLoyalty, loggedIn },
    } = getRequestContext();

    const [{ loading: isRefreshing }, onRefresh] = useAsyncFn(async () => {
        await updateOrderSnapshot({ order_id: orderId, signature, store_slug: storeSlug });
        await refetch();
    }, [signature, orderId]);

    const [register, { error, isLoading }] = useRegisterLoyaltyAndReassignOrder();

    if (!loyalty) {
        return null;
    }

    return (
        <LoyaltyBlockUI
            orderLoyalty={loyalty}
            accountLoyalty={accountLoyalty}
            isLoggedIn={loggedIn}
            isSelected={isSelected}
            isRefreshing={isRefreshing}
            onRefresh={onRefresh}
            onChange={onChange}
            onRegister={register}
            registerError={error}
            isLoadingRegister={isLoading}
        />
    );
};
